import { motion } from 'framer-motion';
import { useRef } from 'react';
import { useInView } from 'framer-motion';

const certifications = [
  { icon: 'ri-global-line', label: 'Verificación Europea', desc: 'ECE R30 / ECE R54', color: 'text-sky-400 bg-sky-500/10 border-sky-500/20' },
  { icon: 'ri-flag-line', label: 'Verificación Americana', desc: 'DOT FMVSS 139', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  { icon: 'ri-shield-check-line', label: 'Seguridad de grado internacional', desc: 'Familia y negocio', color: 'text-[#28A745] bg-[#28A745]/10 border-[#28A745]/20' },
];

const features = [
  { icon: 'ri-speed-up-line', title: 'Rendimiento de alto desempeño', desc: 'Compuesto de caucho de última generación para máxima adherencia en vías mojadas y secas.' },
  { icon: 'ri-water-flash-line', title: 'Evacuación de agua optimizada', desc: 'Diseño de labrado con canales de drenaje que reducen el riesgo de aquaplaning.' },
  { icon: 'ri-temp-cold-line', title: 'Resistencia térmica certificada', desc: 'Estabilidad estructural en condiciones extremas de temperatura y carga.' },
  { icon: 'ri-leaf-line', title: 'Eficiencia de combustible', desc: 'Baja resistencia a la rodadura que reduce el consumo de combustible hasta un 5%.' },
];

export default function ComforsetQuality() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const handleScrollToForm = () => {
    const formSection = document.getElementById('registro-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <section className="py-16 md:py-24 bg-[#1A2B3C]" ref={ref}>
      <div className="max-w-6xl mx-auto px-4">
        <motion.div className="text-center mb-12 md:mb-16" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#28A745]/15 border border-[#28A745]/40 rounded-full text-[#28A745] text-xs font-bold tracking-wider uppercase mb-4">
            <i className="ri-award-line"></i>
            Calidad certificada
          </span>
          <h2 className="text-2xl md:text-4xl font-black text-white leading-tight mb-4 tracking-tight">
            Llantas de{' '}
            <span className="text-[#28A745]">Calidad Internacional</span>
            <br className="hidden md:block" />
            {' '}Verificación Europea y Americana
          </h2>
          <p className="text-white/55 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Seguridad de grado internacional para tu familia o tu negocio. Cada llanta supera los estándares técnicos más exigentes del mercado global.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-12">
          <motion.div className="relative" initial={{ opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="relative rounded-2xl overflow-hidden h-72 md:h-96">
              <img src="https://public.readdy.ai/ai/img_res/e2b915aa744b4977817ffd3602f2df72.jpg" alt="Llantas verificación europea americana calidad premium" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B3C]/60 to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                {certifications.map((cert, index) => (
                  <div key={index} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border backdrop-blur-sm ${cert.color}`}>
                    <i className={`${cert.icon} text-xs`}></i>
                    <div>
                      <p className="text-[10px] font-bold leading-none">{cert.label}</p>
                      <p className="text-[9px] opacity-70 leading-none mt-0.5">{cert.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="space-y-5">
              {features.map((feature, index) => (
                <motion.div key={index} className="flex items-start gap-4 p-4 bg-white/5 border border-white/8 rounded-xl hover:border-[#28A745]/30 transition-all duration-300" initial={{ opacity: 0, x: 20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}>
                  <div className="w-10 h-10 flex items-center justify-center bg-[#28A745]/15 rounded-xl flex-shrink-0">
                    <i className={`${feature.icon} text-[#28A745] text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm mb-1">{feature.title}</h4>
                    <p className="text-white/45 text-xs leading-relaxed">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div className="relative overflow-hidden bg-[#28A745]/10 border border-[#28A745]/25 rounded-2xl p-6 md:p-8 text-center" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.5 }}>
          <div className="w-14 h-14 flex items-center justify-center bg-[#28A745]/20 rounded-full mx-auto mb-4">
            <i className="ri-shield-star-fill text-[#28A745] text-2xl"></i>
          </div>
          <h3 className="text-white font-black text-xl md:text-2xl mb-3 tracking-tight">
            "Seguridad de grado internacional para tu familia o tu negocio"
          </h3>
          <p className="text-white/50 text-sm max-w-xl mx-auto mb-6 leading-relaxed">
            Al adquirir llantas a través del programa, accedes a productos con verificación de seguridad europea y americana, respaldados por la red técnica de GESTIÓN AUTOMOTRIZ AW2020 SAS.
          </p>
          <button onClick={handleScrollToForm} className="inline-flex items-center gap-2 px-7 py-3 bg-[#28A745] hover:bg-[#218838] text-white font-bold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm">
            <i className="ri-car-line text-base"></i>
            Activar beneficios de llantas
          </button>
        </motion.div>
      </div>
    </section>
  );
}
